import styled from "styled-components";
import { tokens } from "../../../public/tokens";

const { colors, typography, globals } = tokens;

/**
 * Base style shared by every data-row cell, at Figma's 56px minimum row height so header rows
 * (unfixed height) stay visually distinct from body rows. `border-box` keeps the vertical padding
 * inside that height, so it only shows once multi-line content (e.g. bullet lists) grows the row.
 */
const dataCell = {
    height: "56px",
    boxSizing: "border-box",
    padding: globals["spacing-2"],
    border: "none",
    borderBottom: `${globals["spacing-px"]} solid ${colors.surfaces["border-divider"]}`,
    fontFamily: typography.fontFamilies["albert-sans"],
    fontSize: typography.fontSize["lg-14px"],
    fontWeight: typography.fontWeights.regular,
    color: colors.text.primary,
    verticalAlign: "middle",
    wordBreak: "break-word",

    "&:first-child": {
        paddingLeft: globals["spacing-4"],
    },
};

export const StyledDataTableRoot = styled.div({
    width: "100%",
});

export const StyledTableWrapper = styled.div({
    width: "100%",
    overflowX: "auto",
});

/**
 * With a pagination footer, the last row drops its bottom border since the footer's own top
 * border already draws that line.
 */
export const StyledTable = styled.table(({ $hasFooter = false }) => ({
    width: "100%",
    border: "none",
    borderCollapse: "collapse",

    "tbody tr:nth-of-type(even)": {
        backgroundColor: colors.surfaces["page-background"],
    },

    ...($hasFooter && {
        "tbody tr:last-child > td": {
            borderBottom: "none",
        },
    }),
}));

export const StyledTh = styled.th(({ $width, $align = "left", $sortable = false }) => ({
    textAlign: $align,
    width: $width,
    padding: `${globals["spacing-2-5"]} ${globals["spacing-2"]}`,
    border: "none",
    borderBottom: `${globals["spacing-px"]} solid ${colors.surfaces["border-divider"]}`,
    color: colors.text.primary,
    fontFamily: typography.fontFamilies["albert-sans"],
    fontSize: typography.fontSize["lg-14px"],
    fontWeight: typography.fontWeights.semibold,
    whiteSpace: "nowrap",
    cursor: $sortable ? "pointer" : "default",
    userSelect: "none",

    "&:first-child": {
        paddingLeft: globals["spacing-4"],
    },
}));

export const StyledSortLabel = styled.span(({ $active = false }) => ({
    display: "inline-flex",
    alignItems: "center",
    padding: `${globals["spacing-1"]} ${globals["spacing-2"]}`,
    margin: `0 calc(-1 * ${globals["spacing-2"]})`,
    borderRadius: globals["border-radius"],
    backgroundColor: $active ? colors.neutral.black : "transparent",
    color: $active ? colors.neutral.white : "inherit",
}));

export const StyledSortIcon = styled.i(({ $active = false }) => ({
    marginLeft: globals["spacing-2-5"],
    fontSize: typography.fontSize["md-12px"],
    color: $active ? colors.neutral.white : colors.neutral.black,
}));

export const StyledHeaderIcon = styled.i(({ $active = false }) => ({
    marginRight: globals["spacing-2-5"],
    fontSize: typography.fontSize["lg-14px"],
    color: $active ? colors.neutral.white : colors.text.primary,
}));

export const StyledTd = styled.td(({ $align = "left" }) => ({
    ...dataCell,
    textAlign: $align,
}));

export const StyledMessageCell = styled.td({
    ...dataCell,
    height: "auto",
    padding: globals["spacing-5"],
    textAlign: "center",
    color: colors.text.secondary,
});
